"use client";
import { useState } from "react";
import Image from "next/image";
import type { BuiltTabsData } from "@/data/industriesBuiltTabs";

type BuiltTabsProps = {
    data: BuiltTabsData;
};

export default function BuiltTabs({ data }: BuiltTabsProps) {
    const [activeId, setActiveId] = useState(data.tabs[0]?.id ?? "");
    const activeTab = data.tabs.find((tab) => tab.id === activeId) || data.tabs[0];
    if (!data.tabs.length) return null;
    return (
        <section className="bg-gradient-to-b from-[#F4F4FD] to-[#ffffff] py-12 xl:py-20">
            <div className="container mx-auto px-4">
                <div className="mb-8 text-center xl:mb-10">
                    <h2 className="text-[24px] lg:text-[32px] mb-2 lg:mb-4 text-center mx-auto max-w-[800px] font-semibold text-[#202020]">
                        {data.sectionTitle}{" "}
                        <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
                            {data.sectionTitleHighlight}
                        </span>
                    </h2>
                    <p className="max-w-[800px] mx-auto text-center text-[#202020]">
                        {data.sectionSubtitle}
                    </p>
                </div>
                <div className="mx-auto mb-8 flex flex-wrap justify-center gap-2 lg:mb-10">
                    {data.tabs.map((tab) => {
                        const isActive = tab.id === activeId;
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveId(tab.id)}
                                className={`rounded-lg border px-3 py-1.5 text-sm font-normal lg:px-4 lg:py-2 ${isActive
                                    ? "border-[#202020] bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent"
                                    : "border-[#E0E0EA] bg-white text-[#4A4A4A] hover:border-[#C5C5D8]"
                                    }`}
                            >
                                {tab.tabLabel}
                            </button>
                        );
                    })}
                </div>
                <div className="relative overflow-hidden rounded-3xl bg-[#F6F5FF] p-4 sm:p-6 lg:p-8">
                    <div className="flex flex-col items-stretch gap-6 xl:flex-row xl:items-center xl:gap-8 xl:justify-between">
                        <div className="w-full xl:w-1/2 xl:max-w-[520px]">
                            {activeTab.badgeLabel && (
                                <div className="inline-flex rounded-full bg-white px-3 py-1 text-sm font-semibold">
                                    <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
                                        {activeTab.badgeLabel}
                                    </span>
                                </div>
                            )}
                            <h3
                                className={`text-[20px] xl:text-[22px] font-semibold text-[#202020] ${activeTab.badgeLabel ? "mt-4" : ""
                                    }`}
                            >
                                {activeTab.title}
                            </h3>
                            <p className="mt-1 text-[#202020]">{activeTab.description}</p>
                            <div className="mt-4 rounded-2xl border border-dashed border-[#D5D3F0] bg-white/60 p-3 lg:p-4">
                                {activeTab.points.map((point, index) => (
                                    <p
                                        key={index}
                                        className="text-[13px] leading-relaxed text-[#717171] xl:text-[14px] mb-2"
                                    >
                                        •{" "}
                                        <span className="font-semibold text-[#202020]">
                                            {point.label}:
                                        </span>{" "}
                                        {point.description}
                                    </p>
                                ))}
                            </div>
                        </div>
                        <div className="relative hidden h-[250px] w-full overflow-hidden rounded-2xl sm:h-[300px] md:h-[350px] lg:h-full lg:w-1/2 xl:block">
                            <Image
                                src={activeTab.image}
                                alt={activeTab.title}
                                width={600}
                                height={400}
                                className="h-full w-full object-cover"
                                priority
                                draggable={false}
                                quality={100}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
